import * as THREE from "three";
import { reachTo, solveLeg } from "../ik";
import type { Skater } from "../../skater";
import type { Arm, Leg, SkaterRig } from "../skater-parts";

// The parts of posing a rider on a ride (#139) that don't depend on which
// ride it is: getting on and off, the arm rate limit, reaching a hand to a
// grip and holding a ride walked beside you. `BikePose` (bike-pose.ts) is
// built on it, and the scooter's and skates' poses go on it too.

/** What a ride's pose reads off the board's pose each frame (eased there). */
export interface RideCue {
  /** The lean into turns. */
  lean: number;
  /** -1 up on the back wheel, 1 the front, eased; and the manual's balance, eased. */
  manualTip: number;
  manualWobble: number;
  /** The air tuck and landing dip springs' values. */
  airTuck: number;
  landDip: number;
}

/** A ride's pose as `SkaterModel` runs it (#145): walked beside you on foot, then ridden. Each returns whether it moved the arms. */
export interface RideRig {
  walkBeside(sk: Skater, dt: number, out: number): boolean;
  apply(sk: Skater, dt: number, cue: Readonly<RideCue>): boolean;
}

/**
 * The grab the hands are in: a held grab, or a body flip's grab (#138)
 * until `FLIP_GRAB_HELD` of the way round, so the hands are back on the
 * bars as it comes round.
 */
export function heldGrab(sk: Skater): string | null {
  const g = sk.grab;
  return g?.timed ? (g.timed.grabbed !== null && g.t < FLIP_GRAB_HELD * g.timed.duration ? g.timed.grabbed : null) : g && g.released === null ? g.name : null;
}

/** How far round a body flip its grab lets go. */
const FLIP_GRAB_HELD = 0.7;

/**
 * Something named eased in and out (a grab, a grind's style): switching
 * to another lets go of the last one first, then takes the new one.
 */
export class Held<T extends string> {
  name: T | null = null;
  value = 0;
  constructor(private readonly rate: number) {}

  update(name: T | null, dt: number): void {
    if (name && name !== this.name && this.value > 0.05) this.value += (0 - this.value) * easeBy(this.rate, dt);
    else {
      if (name) this.name = name;
      this.value += ((name ? 1 : 0) - this.value) * easeBy(this.rate, dt);
    }
  }

  /** How far into `name` the hands are: 0 unless it's the one held. */
  of(name: string): number {
    return this.name === name ? this.value : 0;
  }
}

export class RidePose {
  /** 0..1 on the ride, eased (`ease`). */
  on = 0;
  /** Everything a ride's pose moves on the rider, and where the board's (or walking) pose left it this frame. */
  private jointList: THREE.Object3D[] | null = null;
  private readonly fromP: THREE.Vector3[] = [];
  private readonly fromQ: THREE.Quaternion[] = [];
  /** The arms' shoulders and elbows as the last frame on the ride left them (`limitArms`), and whether that's current. */
  private readonly armJoints: readonly THREE.Object3D[];
  private readonly armPrev = [new THREE.Quaternion(), new THREE.Quaternion(), new THREE.Quaternion(), new THREE.Quaternion()];
  private armPrevSet = false;
  /** The front arm's shoulder and elbow as the last step walking the ride left them (`holdBeside`). */
  private readonly walkArm = [new THREE.Quaternion(), new THREE.Quaternion()] as const;
  /**
   * Where the ride stood walked beside you (`noteBeside`), in the rider's
   * turning group; whether it was there this frame and last; and whether
   * it's sliding from there to under you as you get on (#145).
   */
  private readonly besideP = new THREE.Vector3();
  private readonly besideQ = new THREE.Quaternion();
  private besideNow = false;
  private besideLast = false;
  private mounting = false;
  /**
   * Where the ride was shown under you on the last frame ridden
   * (`mountFrom`), and whether there is one: getting off, it slides from
   * there back out beside you (`dismountTo`, #145).
   */
  private readonly underP = new THREE.Vector3();
  private readonly underQ = new THREE.Quaternion();
  private underSet = false;
  private riding = false;

  constructor(private rig: SkaterRig) {
    this.armJoints = [rig.armFront.shoulder, rig.armFront.elbow, rig.armBack.shoulder, rig.armBack.elbow];
  }

  /**
   * Eases getting on (`on`) or off and returns how far on the rider is.
   * At 0 the ride's pose has nothing to do, and the arm rate limit starts
   * afresh next time.
   */
  ease(on: boolean, dt: number): number {
    const was = this.on;
    this.on = easeHold(this.on, on, 12, dt);
    if (this.on === 0) {
      this.armPrevSet = false;
      this.underSet = false;
    }
    this.riding = on;
    // Getting on a ride that was walked beside you last frame: it comes from there (`mountFrom`).
    if (was === 0 && this.on > 0) this.mounting = this.besideLast;
    if (!on || this.on >= 1) this.mounting = false;
    this.besideLast = this.besideNow;
    this.besideNow = false;
    return this.on;
  }

  /**
   * Whether the ride's going between beside you and under you this frame
   * (#145): getting on one walked beside you, or getting off with it out
   * beside you again (`noteBeside` this frame). Run after `ease`.
   */
  get between(): boolean {
    return this.on > 0 && this.on < 1 && (this.riding ? this.mounting : this.besideLast);
  }

  /** The ride's root is where it's walked beside you this frame (run each frame it's walked, before `ease`). */
  noteBeside(root: THREE.Object3D): void {
    this.besideP.copy(root.position);
    this.besideQ.copy(root.quaternion);
    this.besideNow = true;
  }

  /**
   * Getting on (#145): moves the ride's root, already placed under the
   * rider, back toward where it was walked beside them by how far off it
   * they still are, so it slides under them as they get on.
   */
  mountFrom(root: THREE.Object3D): void {
    if (this.mounting) {
      const w = this.on;
      root.position.lerpVectors(this.besideP, root.position, w);
      root.quaternion.copy(MOUNT_Q.copy(this.besideQ).slerp(root.quaternion, w));
    }
    if (!this.riding) return;
    this.underP.copy(root.position);
    this.underQ.copy(root.quaternion);
    this.underSet = true;
  }

  /**
   * Getting off (#145): moves the ride's root, already placed beside the
   * rider where it's walked, back toward where it was last under them by
   * how far on they still are, so it slides out beside them as they step
   * off. Run after `ease`, each frame it's walked.
   */
  dismountTo(root: THREE.Object3D): void {
    if (this.riding || !this.underSet || this.on <= 0) return;
    const w = this.on;
    root.position.lerp(this.underP, w);
    root.quaternion.slerp(this.underQ, w);
  }

  /** Remembers the rider's joints as the board's (or walking) pose left them, before the ride's pose goes on. */
  saveJoints(): void {
    const joints = this.joints();
    for (let i = 0; i < joints.length; i++) {
      this.fromP[i]!.copy(joints[i]!.position);
      this.fromQ[i]!.copy(joints[i]!.quaternion);
    }
  }

  /** Blends the rider from the saved joints to the ride's pose by how far on they are (getting on or off). */
  blendJoints(): void {
    if (this.on >= 1) return;
    const w = this.on;
    const joints = this.joints();
    for (let i = 0; i < joints.length; i++) {
      const o = joints[i]!;
      o.position.lerpVectors(this.fromP[i]!, o.position, w);
      o.quaternion.copy(this.fromQ[i]!.slerp(o.quaternion, w));
    }
  }

  /**
   * Turns the shoulders and elbows no faster than `RIDE_ARM_RATE` from the
   * last frame's pose. An X-Up (#138) swings the bars round faster than
   * the arms can follow: a grip passes close in to the shoulder and back
   * out, and the two-bone IK swings an elbow ~60° in a frame to follow it,
   * so the hand trails the grip for a few frames instead. Off (`on` false)
   * for a bail (its own flailing arms) and while getting on or off.
   */
  limitArms(on: boolean, dt: number): void {
    const arms = this.armJoints;
    const step = RIDE_ARM_RATE * dt;
    for (let i = 0; i < arms.length; i++) {
      const q = arms[i]!.quaternion;
      const prev = this.armPrev[i]!;
      if (on && this.armPrevSet) {
        const angle = prev.angleTo(q);
        if (angle > step) q.copy(prev.slerp(q, step / angle));
      }
      prev.copy(q);
    }
    this.armPrevSet = on;
  }

  /**
   * The front arm holding a ride walked beside the rider (#145): its hand
   * on `grip` (world space; the caller brings the rig's matrices up to
   * date), blended `w` (0..1) from wherever the walk left the arm. With no
   * `grip` (just got on, or put back in the pack) it eases `w` of the way
   * back to where the last step walking left it, so getting on blends the
   * arm from the grip.
   */
  holdBeside(grip: THREE.Vector3 | null, w: number): void {
    const arm = this.rig.armFront;
    if (!grip) {
      arm.shoulder.quaternion.slerp(this.walkArm[0], w);
      arm.elbow.quaternion.slerp(this.walkArm[1], w);
      return;
    }
    gripWith(arm, grip, w, false);
    this.walkArm[0].copy(arm.shoulder.quaternion);
    this.walkArm[1].copy(arm.elbow.quaternion);
  }

  private joints(): THREE.Object3D[] {
    if (!this.jointList) {
      const arms = [this.rig.armFront, this.rig.armBack].flatMap((a) => [a.shoulder, a.elbow]);
      const legs = this.rig.legs.flatMap((l) => [l.hip, l.knee, l.foot]);
      this.jointList = [this.rig.rider, this.rig.hips, this.rig.torso, this.rig.head, ...arms, ...legs];
      for (let i = 0; i < this.jointList.length; i++) {
        this.fromP.push(new THREE.Vector3());
        this.fromQ.push(new THREE.Quaternion());
      }
    }
    return this.jointList;
  }
}

/**
 * Reaches `arm`'s hand to `target` (world space), blended `w` (0..1) from
 * the arm's pose before. `ahead`: the target's out in front (bars), where
 * the arm's usual pole would line up with the reach, so the elbow points
 * out to its own side instead.
 */
export function gripWith(arm: Arm, target: THREE.Vector3, w: number, ahead: boolean): void {
  const freeShoulder = FREE_SHOULDER.copy(arm.shoulder.quaternion);
  const freeElbow = FREE_ELBOW.copy(arm.elbow.quaternion);
  if (ahead) {
    const pole = POLE.copy(arm.pole);
    arm.pole.set(0.3, 0, Math.sign(arm.shoulder.position.z)).normalize();
    reachTo(arm, target);
    arm.pole.copy(pole);
  } else reachTo(arm, target);
  if (w < 1) {
    arm.shoulder.quaternion.copy(freeShoulder.slerp(arm.shoulder.quaternion, w));
    arm.elbow.quaternion.copy(freeElbow.slerp(arm.elbow.quaternion, w));
  }
}

/**
 * `solveLeg`, with the foot `ahead` of the hip as well as `down` below it
 * (#138: on a pedal going round): the leg solved to the full reach, then
 * swung forward at the hip, the foot turned back to stay level.
 */
export function footTo(leg: Leg, down: number, ahead: number, shiftZ: number): void {
  solveLeg(leg, Math.hypot(down, ahead), shiftZ);
  const swing = Math.atan2(ahead, down);
  leg.hip.rotation.z -= swing;
  leg.foot.rotation.z += swing;
}

/** The share of the way to its target an eased value moves in `dt` at `rate`. */
export function easeBy(rate: number, dt: number): number {
  return 1 - Math.exp(-rate * dt);
}

/** A 0..1 weight eased toward 1 (`on`) or 0 at `rate`, landing exactly on it (getting on, a ride or the gun out). */
export function easeHold(v: number, on: boolean, rate: number, dt: number): number {
  const to = on ? 1 : 0;
  v += (to - v) * easeBy(rate, dt);
  return Math.abs(v - to) < 1e-3 ? to : v;
}

/** How fast (radians a second) a shoulder or elbow can turn on a ride (see `limitArms`): 18° a frame at 60 fps. */
const RIDE_ARM_RATE = (18 * 60 * Math.PI) / 180;
const FREE_SHOULDER = new THREE.Quaternion();
const MOUNT_Q = new THREE.Quaternion();
const FREE_ELBOW = new THREE.Quaternion();
const POLE = new THREE.Vector3();
